import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiTruck,
  FiUser,
  FiDollarSign,
  FiMapPin,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiAlertTriangle,
  FiCheckCircle,
  FiXCircle,
  FiNavigation,
  FiNavigation2,
  FiGauge,
  FiDroplet,
  FiMessageSquare
} from 'react-icons/fi';
import api from '../utils/axios';
import toast from 'react-hot-toast';

const ReservationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchReservationData();
  }, [id]);

  const fetchReservationData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/reservations/${id}`);
      setReservation(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reservation data:', error);
      toast.error('Échec de la récupération des données de réservation');
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/reservations/${id}`);
      toast.success('Réservation supprimée avec succès');
      navigate('/reservations');
    } catch (error) {
      console.error('Error deleting reservation:', error);
      toast.error('Échec de la suppression de la réservation');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await api.put(`/api/reservations/${id}/status`, { status: newStatus });
      toast.success(`Statut de la réservation mis à jour vers ${newStatus}`);
      fetchReservationData(); // Refresh data
    } catch (error) {
      console.error('Error updating reservation status:', error);
      toast.error('Échec de la mise à jour du statut de la réservation');
    }
  };

  const handlePaymentStatusUpdate = async (newPaymentStatus) => {
    try {
      await api.put(`/api/reservations/${id}/payment-status`, { paymentStatus: newPaymentStatus });
      toast.success(`Statut de paiement mis à jour vers ${newPaymentStatus}`);
      fetchReservationData(); // Refresh data
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Échec de la mise à jour du statut de paiement');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Confirmed': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'Active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      case 'Cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Overdue': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Partial': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'Unpaid': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'Refunded': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getFuelLevelColor = (level) => {
    switch (level) {
      case 'Full': return 'text-green-600';
      case 'Three Quarters': return 'text-green-500';
      case 'Half': return 'text-yellow-600';
      case 'Quarter': return 'text-orange-600';
      case 'Empty': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="text-center py-12">
        <FiCalendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400">Réservation non trouvée</p>
        <Link to="/reservations" className="btn btn-primary mt-4">Retour aux Réservations</Link>
      </div>
    );
  }

  const isOverdue = reservation.status === 'Active' && new Date() > new Date(reservation.endDate);
  const overdueDays = isOverdue ? Math.ceil((new Date() - new Date(reservation.endDate)) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Link to="/reservations" className="btn btn-secondary text-sm w-fit">
            <FiArrowLeft className="mr-1" />
            <span className="hidden sm:inline">Retour aux Réservations</span>
            <span className="sm:hidden">Retour</span>
          </Link>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Détails de la Réservation</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Afficher et gérer les informations de réservation</p>
          </div>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn btn-danger flex items-center gap-2 text-sm w-fit"
          title="Supprimer la réservation"
        >
          <FiTrash2 className="w-4 h-4" />
          <span className="hidden sm:inline">Supprimer</span>
          <span className="sm:hidden">Suppr.</span>
        </button>
      </div>

      {/* Status Banner */}
      {isOverdue && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FiAlertTriangle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-medium text-red-900 dark:text-red-100">Réservation en Retard</h3>
              <p className="text-sm text-red-700 dark:text-red-300">
                Cette réservation est en retard de {overdueDays} jour{overdueDays !== 1 ? 's' : ''}.
                Veuillez contacter le client pour un retour immédiat.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Overview Card */}
      <div className="card p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0">
              <FiCalendar className="w-7 h-7 sm:w-8 sm:h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                Réservation #{reservation._id.slice(-6).toUpperCase()}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {reservation.car?.brand} {reservation.car?.model} • {reservation.client?.name}
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2">
                <span className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <FiCalendar className="w-4 h-4 flex-shrink-0" />
                  {formatDate(reservation.startDate)} - {formatDate(reservation.endDate)}
                </span>
                <span className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  <FiClock className="w-4 h-4 flex-shrink-0" />
                  {reservation.duration || 0} jour{reservation.duration !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 lg:flex-col lg:items-end">
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(reservation.status)}`}>
              {reservation.status}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
              {reservation.paymentStatus}
            </span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 mb-6">
          {reservation.status === 'Confirmed' && (
            <button
              onClick={() => handleStatusUpdate('Active')}
              className="btn btn-primary text-xs sm:text-sm"
              title="Démarrer la location de voiture"
            >
              <span className="hidden sm:inline">Démarrer la Location</span>
              <span className="sm:hidden">Démarrer</span>
            </button>
          )}
          {reservation.status === 'Active' && (
            <button
              onClick={() => handleStatusUpdate('Completed')}
              className="btn btn-success text-xs sm:text-sm opacity-50 cursor-not-allowed"
              title="Le statut 'Completed' est appliqué automatiquement par le système lorsque la date de fin est dépassée"
              disabled
            >
              ⏰ <span className="hidden sm:inline">Terminer la Location (Auto)</span>
              <span className="sm:hidden">Terminer</span>
            </button>
          )}
          {reservation.status === 'Pending' && (
            <button
              onClick={() => handleStatusUpdate('Confirmed')}
              className="btn btn-secondary text-xs sm:text-sm"
              title="Confirmer la réservation de voiture"
            >
              <span className="hidden sm:inline">Confirmer la Réservation</span>
              <span className="sm:hidden">Confirmer</span>
            </button>
          )}
          {reservation.status === 'Pending' && (
            <button
              onClick={() => handleStatusUpdate('Cancelled')}
              className="btn btn-danger text-xs sm:text-sm"
              title="Annuler la réservation de voiture"
            >
              <span className="hidden sm:inline">Annuler la Réservation</span>
              <span className="sm:hidden">Annuler</span>
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <nav className="flex space-x-2 sm:space-x-6 min-w-max">
            {['overview', 'car', 'client', 'payment', 'notes'].map((tab) => {
              const tabLabels = {
                overview: 'Aperçu',
                car: 'Voiture',
                client: 'Client',
                payment: 'Paiement',
                notes: 'Notes'
              };
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-2 sm:py-3 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm capitalize whitespace-nowrap ${activeTab === tab
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                >
                  {tabLabels[tab]}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations de Location</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date de début :</span>
                    <span className="font-medium">{formatDate(reservation.startDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date de fin :</span>
                    <span className="font-medium">{formatDate(reservation.endDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Durée :</span>
                    <span className="font-medium">{reservation.duration || 0} jour{reservation.duration !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Prix journalier :</span>
                    <span className="font-medium">{reservation.dailyPrice?.toLocaleString()} MAD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Prix total :</span>
                    <span className="font-medium text-lg text-green-600">{reservation.totalPrice?.toLocaleString()} MAD</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Dépôt :</span>
                    <span className="font-medium">{reservation.deposit?.toLocaleString() || '0'} MAD</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Lieu et Détails</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Lieu de départ :</span>
                    <span className="font-medium">{reservation.pickupLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Lieu de retour :</span>
                    <span className="font-medium">{reservation.returnLocation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Méthode de paiement :</span>
                    <span className="font-medium">{reservation.paymentMethod}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Créé le :</span>
                    <span className="font-medium">{formatDate(reservation.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Créé par :</span>
                    <span className="font-medium">{reservation.createdBy?.name || 'N/A'}</span>
                  </div>
                  {reservation.secondDriver && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Deuxième conducteur :</span>
                      <span className="font-medium text-blue-600 dark:text-blue-400">🚗 {reservation.secondDriver.name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Car Tab */}
          {activeTab === 'car' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations du Véhicule</h3>
              {reservation.car ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Détails de la Voiture</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Marque :</span>
                        <span className="font-medium">{reservation.car.brand}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Modèle :</span>
                        <span className="font-medium">{reservation.car.model}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Plaque d'immatriculation :</span>
                        <span className="font-medium">{reservation.car.licensePlate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Année :</span>
                        <span className="font-medium">{reservation.car.year || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Détails de Location</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kilométrage initial :</span>
                        <span className="font-medium">{reservation.initialMileage?.toLocaleString() || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Kilométrage final :</span>
                        <span className="font-medium">{reservation.finalMileage?.toLocaleString() || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Carburant initial :</span>
                        <span className={`font-medium ${getFuelLevelColor(reservation.fuelLevel)}`}>
                          {reservation.fuelLevel}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Carburant final :</span>
                        <span className={`font-medium ${getFuelLevelColor(reservation.finalFuelLevel)}`}>
                          {reservation.finalFuelLevel || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiTruck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Informations de la voiture non disponibles</p>
                </div>
              )}
            </div>
          )}

          {/* Client Tab */}
          {activeTab === 'client' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations du Client</h3>
              {reservation.client ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Détails Personnels</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Nom :</span>
                        <span className="font-medium">{reservation.client.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Email :</span>
                        <span className="font-medium">{reservation.client.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Téléphone :</span>
                        <span className="font-medium">{reservation.client.phone}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Permis de conduire :</span>
                        <span className="font-medium">{reservation.client.driverLicenseNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">CIN :</span>
                        <span className="font-medium">{reservation.client.cin || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Adresse</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Rue :</span>
                        <span className="font-medium">{reservation.client.address?.street}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Ville :</span>
                        <span className="font-medium">{reservation.client.address?.city}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">État/Région :</span>
                        <span className="font-medium">{reservation.client.address?.state}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Code postal :</span>
                        <span className="font-medium">{reservation.client.address?.zipCode}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiUser className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Informations du client non disponibles</p>
                </div>
              )}

              {/* Second Driver Section */}
              {reservation.secondDriver && (
                <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    🚗 Deuxième Conducteur
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Détails Personnels</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Nom :</span>
                          <span className="font-medium">{reservation.secondDriver.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Email :</span>
                          <span className="font-medium">{reservation.secondDriver.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Téléphone :</span>
                          <span className="font-medium">{reservation.secondDriver.phone}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Permis de conduire :</span>
                          <span className="font-medium">{reservation.secondDriver.driverLicenseNumber}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white mb-3">Adresse</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Rue :</span>
                          <span className="font-medium">{reservation.secondDriver.address?.street}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Ville :</span>
                          <span className="font-medium">{reservation.secondDriver.address?.city}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">État/Région :</span>
                          <span className="font-medium">{reservation.secondDriver.address?.state}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Code postal :</span>
                          <span className="font-medium">{reservation.secondDriver.address?.zipCode}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Tab */}
          {activeTab === 'payment' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations de Paiement</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Détails de Paiement</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Statut de paiement :</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(reservation.paymentStatus)}`}>
                        {reservation.paymentStatus}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Méthode de paiement :</span>
                      <span className="font-medium">{reservation.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prix journalier :</span>
                      <span className="font-medium">{reservation.dailyPrice?.toLocaleString()} MAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Prix total :</span>
                      <span className="font-medium text-lg text-green-600">{reservation.totalPrice?.toLocaleString()} MAD</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Dépôt :</span>
                      <span className="font-medium">{reservation.deposit?.toLocaleString() || '0'} MAD</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">Actions de Paiement</h4>
                  <div className="space-y-3">
                    <button
                      onClick={() => handlePaymentStatusUpdate('Paid')}
                      disabled={reservation.paymentStatus === 'Paid'}
                      className="btn btn-success w-full"
                    >
                      Marquer comme Payé
                    </button>
                    <button
                      onClick={() => handlePaymentStatusUpdate('Partial')}
                      disabled={reservation.paymentStatus === 'Partial'}
                      className="btn btn-secondary w-full"
                    >
                      Marquer comme Partiel
                    </button>
                    <button
                      onClick={() => handlePaymentStatusUpdate('Refunded')}
                      disabled={reservation.paymentStatus === 'Refunded'}
                      className="btn btn-info w-full"
                    >
                      Marquer comme Remboursé
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes et Informations Supplémentaires</h3>
              {reservation.notes ? (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{reservation.notes}</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiMessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">Aucune note disponible pour cette réservation</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <FiAlertTriangle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Supprimer la Réservation</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action ne peut pas être annulée.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
              >
                Supprimer la Réservation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationDetail; 